import { extractEpsgFromWkt } from './extractEpsgFromWkt.js';

/**
 * @typedef {Object} LazHeaderInfo
 * @property {number} versionMajor
 * @property {number} versionMinor
 * @property {number} headerSize
 * @property {number} offsetToPointData
 * @property {[number, number, number]} scale
 * @property {[number, number, number]} offset
 * @property {{min: [number, number, number], max: [number, number, number]}} bbox
 * @property {[number, number, number]} center
 * @property {string=} wkt
 * @property {number=} epsg
 */

function readFixedString(dv, offset, length) {
  const bytes = new Uint8Array(dv.buffer, dv.byteOffset + offset, length);
  const end = bytes.indexOf(0);
  return new TextDecoder('ascii').decode(end >= 0 ? bytes.subarray(0, end) : bytes).trim();
}

function readUint64(dv, offset, field) {
  const value = dv.getBigUint64(offset, true);
  if (value > BigInt(Number.MAX_SAFE_INTEGER)) {
    throw new Error(`${field} exceeds the supported file offset range.`);
  }
  return Number(value);
}

function validateRange(start, length, limit, description) {
  if (!Number.isSafeInteger(start) || !Number.isSafeInteger(length) ||
      start < 0 || length < 0 || start > limit || length > limit - start) {
    throw new Error(`Invalid or truncated ${description}.`);
  }
}

async function readRange(file, start, length, description) {
  validateRange(start, length, file.size, description);
  const buffer = await file.slice(start, start + length).arrayBuffer();
  if (buffer.byteLength !== length) {
    throw new Error(`Truncated ${description}.`);
  }
  return buffer;
}

async function readWktRecords(file, start, count, limit, extended = false) {
  const headerLength = extended ? 60 : 54;
  const kind = extended ? 'EVLR' : 'VLR';
  validateRange(start, count * headerLength, limit, `${kind} headers`);
  let position = start;
  let wkt;

  // Respect the declared record count: padding before point data is not a VLR.
  for (let index = 0; index < count; index += 1) {
    validateRange(position, headerLength, limit, `${kind} header`);
    const header = new DataView(await readRange(file, position, headerLength, `${kind} header`));
    const userId = readFixedString(header, 2, 16);
    const recordId = header.getUint16(18, true);
    const length = extended
      ? readUint64(header, 20, 'EVLR payload length')
      : header.getUint16(20, true);
    const dataStart = position + headerLength;
    validateRange(dataStart, length, limit, `${kind} payload`);

    if (userId === 'LASF_Projection' && recordId === 2112 && length > 0) {
      const buffer = await readRange(file, dataStart, length, `${kind} WKT`);
      const text = new TextDecoder('utf-8').decode(buffer).replace(/\0+$/, '').trim();
      if (text) wkt = text;
    }
    // Unrelated payloads, including large EVLRs, need not be loaded into memory.
    position = dataStart + length;
  }
  return wkt;
}

/**
 * Read LAS/LAZ bounds and CRS WKT using only uncompressed metadata.
 * VLRs precede point data; LAS 1.4 EVLRs can follow it. Slice each record
 * directly so compressed point data and unrelated payloads are never read.
 *
 * @param {File} file
 * @returns {Promise<LazHeaderInfo>}
 */
export async function readLazHeaderInfo(file) {
  // Bounds end at byte 227 in all supported LAS versions.
  const dv = new DataView(await readRange(file, 0, 227, 'LAS header'));
  if (readFixedString(dv, 0, 4) !== 'LASF') {
    throw new Error('Not a valid LAS/LAZ file (missing LASF signature).');
  }

  const versionMajor = dv.getUint8(24);
  const versionMinor = dv.getUint8(25);
  if (versionMajor !== 1) {
    throw new Error(`Unsupported LAS version ${versionMajor}.${versionMinor}.`);
  }
  const hasExtendedHeader = versionMinor >= 4;
  const minimumHeaderSize = hasExtendedHeader ? 375 : versionMinor === 3 ? 235 : 227;
  const headerSize = dv.getUint16(94, true);
  const offsetToPointData = dv.getUint32(96, true);
  const vlrCount = dv.getUint32(100, true);

  if (headerSize < minimumHeaderSize || headerSize > offsetToPointData || offsetToPointData > file.size) {
    throw new Error(`Invalid LAS header size or point data offset: ${headerSize}, ${offsetToPointData}.`);
  }

  let evlrStart = 0;
  let evlrCount = 0;
  if (hasExtendedHeader) {
    const extension = new DataView(await readRange(file, 227, 148, 'LAS extended header'));
    // Absolute byte offsets 235 (uint64) and 243 (uint32) in LAS 1.4.
    evlrStart = readUint64(extension, 8, 'EVLR start offset');
    evlrCount = extension.getUint32(16, true);
    if (evlrCount > 0 && evlrStart < offsetToPointData) {
      throw new Error('Invalid EVLR start offset: overlaps the LAS header or VLRs.');
    }
  }

  const vlrWkt = await readWktRecords(file, headerSize, vlrCount, offsetToPointData);
  const evlrWkt = evlrCount > 0
    ? await readWktRecords(file, evlrStart, evlrCount, file.size, true)
    : undefined;
  const wkt = evlrWkt || vlrWkt;
  const epsg = extractEpsgFromWkt(wkt) ?? undefined;

  // Fixed LAS offsets: scales @ 131, offsets @ 155, bounds @ 179.
  const scale = [dv.getFloat64(131, true), dv.getFloat64(139, true), dv.getFloat64(147, true)];
  const offset = [dv.getFloat64(155, true), dv.getFloat64(163, true), dv.getFloat64(171, true)];
  const min = [dv.getFloat64(187, true), dv.getFloat64(203, true), dv.getFloat64(219, true)];
  const max = [dv.getFloat64(179, true), dv.getFloat64(195, true), dv.getFloat64(211, true)];
  const center = min.map((value, axis) => (value + max[axis]) / 2);

  return {
    versionMajor,
    versionMinor,
    headerSize,
    offsetToPointData,
    scale,
    offset,
    bbox: { min, max },
    center,
    wkt,
    epsg,
  };
}
